import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { formattedDatePp } from "../../utils/common_lib";

export const TableListing = memo(({
  listData = [],
  cols = [],
  actions = [],
  loading = false,
  groupedListData = null,
  sectionRowClassName = "bg-gray-100 border-b border-gray-200",
  sectionCellClassName = "py-2 px-3 text-sm font-bold text-gray-800",
  tableClassName = "",
}) => {
  const [data, setData] = useState(listData);
  const useGrouped = groupedListData != null && Array.isArray(groupedListData) && groupedListData.length > 0;

  useEffect(() => {
    if (!useGrouped) setData(listData);
  }, [listData, useGrouped]);

  const handleCheckboxChange = useCallback((e, item, itemIndex, col) => {
    try {
      const checked = e.target.checked;
      const newData = [...data];
      newData[itemIndex] = { ...newData[itemIndex], [col.key]: checked };
      setData(newData);
      if (typeof col.onChange === "function") {
        col.onChange(checked, item, itemIndex);
      }
    } catch (error) {
      console.error("Error handling checkbox change:", error);
    }
  }, [data]);

  const getColData = useCallback((item, col, itemIndex) => {
    if (col.checkbox === true) {
      return (
        <input
          type="checkbox"
          checked={!!item[col.key]}
          onChange={(e) => handleCheckboxChange(e, item, itemIndex, col)}
          disabled={col?.disabled || false}
        />
      );
    }
    if (typeof col.getData === "function") {
      return col.getData(item, itemIndex);
    }
    if (col.type === "date") {
      return formattedDatePp(item[col.key], col.format || null);
    }
    if (col.type === "amount") {
      return `$${item[col.key] || "0"}`;
    }
    return item[col.key] || "";
  }, [handleCheckboxChange]);

  const renderDataRow = useCallback((item, itemIndex) => (
    <tr key={item.id != null ? `row_${item.id}` : `row_${itemIndex}`} className={`${itemIndex % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100 border border-gray-100`}>
      {cols.map((col, colIndex) => (
        <td key={`cell_${itemIndex}_${colIndex}`} className={`py-4 px-3 text-sm font-medium text-gray-900 ${col?.tdClass || ""}`}>
          <div
            title={
              col?.skipColHtmlTooltip === true &&
              typeof col?.skipColHtmlTooltip === "boolean"
                ? null
                : col?.title
            }
            onClick={() => {
              if (typeof col.onClick === "function") {
                col.onClick(item, col);
              }
            }}
            className={`${col.onClick ? "cursor-pointer text-primary" : ""} ${col?.colClass || ""}`}
          >
            {getColData(item, col, itemIndex)}
          </div>
        </td>
      ))}
      {actions ? (
        <td className="flex-none">
          <span>
            <button className="list-action-button">
            <i className="fa fa-ellipsis-v fa-lg"></i>
          </button>
          </span>
        </td>
      ) : (
        ""
      )}
    </tr>
  ), [cols, actions, getColData]);

  const renderedTableRows = useMemo(() => {
    if (useGrouped) {
      let globalIndex = 0;
      return groupedListData.flatMap((group, groupIndex) => {
        const sectionRow = (
          <tr key={`section_${groupIndex}`} className={sectionRowClassName}>
            <td colSpan={cols.length + (actions ? 1 : 0)} className={sectionCellClassName}>
              {typeof group.sectionLabel === "function" ? group.sectionLabel(group) : group.sectionLabel}
            </td>
          </tr>
        );
        const dataRows = (group.data || []).map((item, i) => {
          const row = renderDataRow(item, globalIndex);
          globalIndex += 1;
          return row;
        });
        return [sectionRow, ...dataRows];
      });
    }
    return data.map((item, itemIndex) => renderDataRow(item, itemIndex));
  }, [useGrouped, useGrouped ? groupedListData : data, cols, actions, renderDataRow, sectionRowClassName, sectionCellClassName]);

  const renderedTableHeaders = useMemo(() => {
    return (
      <tr className="border border-gray-200">
        {cols.map((col, colIndex) => (
          <th
            key={`col_title_${colIndex}`}
            scope="col"
            className={`py-3.5 px-3 text-${col?.align || "left"} text-sm text-slate-500 ${col?.thClass || ""}`}
          >
            {col.skipColTitle ? "" : col.title}
          </th>
        ))}
        {actions ? <th className="flex-none"></th> : ""}
      </tr>
    );
  }, [cols, actions]);

  return (
    <>
      <div className="flow-root">
        <div className="-mx-2 -my-2 overflow-auto sm:-mx-4 lg:-mx-6">
          <div className="inline-block min-w-full py-2 align-middle sm:px-4 lg:px-6">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className={`min-w-full divide-y divide-gray-300 custom-table ${tableClassName}`}>
                <thead className="bg-gray-50">
                  {renderedTableHeaders}
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={cols.length + 1} className="text-center py-2">
                        Loading...
                      </td>
                    </tr>
                  ) : (
                    renderedTableRows
                  )}
                </tbody>
              </table>
              {!loading && (useGrouped
                ? groupedListData.every((g) => !(g.data?.length > 0))
                : !(data?.length > 0)
              ) && (
                <div className="p-2">No record found.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});